import { RealtimeClient } from '@openai/realtime-api-beta';
import { ItemType } from '@openai/realtime-api-beta/dist/lib/client.js';
import { AudioIO } from '../utils/audio.js';

/**
 * Type for all event logs
 */
export interface RealtimeEvent {
  time: string;
  source: 'client' | 'server';
  count?: number;
  event: { [key: string]: any };
}

export interface AgentMessage {
  id: string;
  type: 'message' | 'error';
  data: { [key: string]: any };
}

export interface TextIO {
  setItems: (items: AgentMessage[]) => void;
}

function getLocalDateTime(): string {
  return new Date().toLocaleString('sv').replace(',', '');
}

export class Agent {
  // keep the date of the item
  private interruptedItems: Set<string> = new Set();

  constructor(
    private client: RealtimeClient,
    private audioIO: AudioIO,
    private textIO: TextIO,
    private lastEventTime: number = Date.now(),
  ) {
    this.setupEventHandlers();
  }

  public getLastEventTime() {
    return this.lastEventTime;
  }

  private setupEventHandlers() {
    this.client.on('realtime.event', (realtimeEvent: any) => {
      this.lastEventTime = Date.now();
      if (realtimeEvent?.event?.type === `response.created`) {
      }
      this.onRealtimeEvent(realtimeEvent);
    });

    this.client.on('error', (event: any) => console.error(event));

    this.client.on('conversation.interrupted', async () => {
      const trackSampleOffset = await this.audioIO.interruptAudioPlayback();
      if (trackSampleOffset?.trackId) {
        const { trackId, offset } = trackSampleOffset;
        this.client.cancelResponse(trackId, offset);
      }
    });

    this.client.on('conversation.updated', async ({ item, delta }: any) => {
      const items = this.client.conversation.getItems();
      if (delta?.audio) {
        this.audioIO.add16BitPCM(delta.audio, item.id);
      }
      if (item.status === 'completed' && item.formatted.audio?.length) {
        // const wavFile = await WavRecorder.decode(
        //   item.formatted.audio,
        //   24000,
        //   24000
        // );
        // item.formatted.file = wavFile;
      }
      this.setItems(items);
    });

    // replace the default handler with a custom one
    this.client.conversation.EventProcessors['conversation.item.truncated'] = ({ item_id, audio_end_ms }: any) => {
      const item = this.client.conversation.getItem(item_id);
      const defaultFrequency = this.client.conversation.defaultFrequency;
      if (!item) {
        throw new Error(`item.truncated: Item "${item_id}" not found`);
      }
      const endIndex = Math.floor((audio_end_ms * defaultFrequency) / 1000);
      // item.formatted.transcript = '';
      item.formatted.audio = item.formatted.audio?.slice(0, endIndex);
      this.interruptedItems.add(item_id);
      return { item, delta: null };
    };
  }

  public isInterrupted(item_id: string) {
    return this.interruptedItems.has(item_id);
  }

  public setTextIO(textIO: TextIO) {
    this.textIO = textIO;
    this.setItems(this.client.conversation.getItems());
  }

  public setItems(items: ItemType[]) {
    let conversation_items: AgentMessage[] = [];
    conversation_items.push({
      id: 'initial',
      type: 'message',
      data: { role: 'assistant', content: 'Hello my fellow!', is_interrupted: false }
    });
    for (let item of items) {
      const messageContent = item.formatted.transcript ||
        (item.role === 'user' && item.formatted.audio?.length ? '(awaiting transcript)' : item.formatted.text) || '';

      conversation_items.push({
        id: item.id,
        type: 'message',
        data: { role: item.role, content: messageContent, is_interrupted: this.isInterrupted(item.id) }
      });
    }
    this.textIO.setItems(conversation_items);
  }

  public onRealtimeEvent(event: any) {
    // Skip handling realtime events
  }

  public reset() {
    this.client.reset();
  }

  public getItems() {
    return this.client.conversation.getItems();
  }

  public deleteItem(id: string) {
    this.client.deleteItem(id);
  }

  public updateSession(turnDetectionType: string | undefined = undefined) {
    this.client.realtime.send('session.update', { 
      datetime: getLocalDateTime(),
      turn_detection: turnDetectionType !== undefined ? (
        turnDetectionType === 'none' ? null : { type: 'server_vad' }) : undefined,
    });
  }

  public updateTurnDetection(type: string) {
    this.updateSession(type);
  }
}
